/**
 * DeviceIdentityService.ts — OPERATOR IDENTITY™ Phase 5 (Build 2.10.0)
 *
 * Stable per-device identity for the Universal Operator Session™.
 * A device is a browser installation: the id is generated once and kept in
 * localStorage. It never leaves the device except as a diagnostic label —
 * the SERVER session list (cookie-based) is the authority on signed-in
 * devices; this id exists so diagnostics and continuity records can honestly
 * say WHICH device wrote them.
 *
 * Honest data only: the device label is parsed from the real User-Agent;
 * when unknown, we say "Unknown", never invent.
 */

const DEVICE_ID_KEY = 'nomad:device-id:v1';

function hasStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

/** Stable device id — created on first call, then permanent for this browser. */
export function getDeviceId(): string {
  if (!hasStorage()) return 'unknown-device';
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id = `dev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return 'unknown-device';
  }
}

/** Regenerate the device id — used by "Simulate New Device" diagnostics. */
export function regenerateDeviceId(): string {
  try { localStorage.removeItem(DEVICE_ID_KEY); } catch { /* ignore */ }
  return getDeviceId();
}

export interface DeviceDescription {
  browser: string;
  os: string;
  /** e.g. "Chrome on Windows" */
  label: string;
  formFactor: 'mobile' | 'tablet' | 'desktop' | 'unknown';
}

/** Parse a User-Agent into an honest human label. Exported for tests. */
export function describeUserAgent(ua: string | null | undefined): DeviceDescription {
  if (!ua) return { browser: 'Unknown', os: 'Unknown', label: 'Unknown Device', formFactor: 'unknown' };
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /OPR\/|Opera/.test(ua) ? 'Opera' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os =
    /Android/.test(ua) ? 'Android' :
    /iPhone|iPad|iPod/.test(ua) ? 'iOS' :
    /Windows/.test(ua) ? 'Windows' :
    /Mac OS X/.test(ua) ? 'macOS' :
    /Linux/.test(ua) ? 'Linux' : 'Unknown OS';
  const formFactor: DeviceDescription['formFactor'] =
    /iPad|Tablet/.test(ua) ? 'tablet' :
    /Mobi|Android|iPhone/.test(ua) ? 'mobile' :
    /Windows|Mac OS X|Linux/.test(ua) ? 'desktop' : 'unknown';
  return { browser, os, label: `${browser} on ${os}`, formFactor };
}

/** Description of THIS device from the live navigator (no fabrication). */
export function describeThisDevice(): DeviceDescription {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : null;
  return describeUserAgent(ua);
}
